import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Eye, MessageSquare, Clock, AlertTriangle, ThumbsUp, ThumbsDown } from 'lucide-react';

interface ReviewItem {
  id: string;
  post_title: string;
  client: string;
  post_type: 'image' | 'video' | 'carousel' | 'story';
  content: string;
  media_urls: string[];
  platforms: string[];
  created_by: string;
  assigned_reviewer: string;
  status: 'pending_review' | 'approved' | 'rejected' | 'revision_requested';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  submitted_at: string;
  review_notes?: string;
  checklist_items: {
    brand_compliance: boolean;
    content_accuracy: boolean;
    visual_quality: boolean;
    platform_specs: boolean;
    legal_compliance: boolean;
  };
}

export default function QualityReview() {
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ReviewItem | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'pending' | 'completed'>('pending');

  useEffect(() => {
    // Mock data
    setReviewItems([
      {
        id: '1',
        post_title: 'New Product Launch Campaign',
        client: 'TechCorp Solutions',
        post_type: 'carousel',
        content: 'Exciting news! Our latest AI-powered analytics dashboard is here. Transform your data into actionable insights with our cutting-edge technology. #TechInnovation #Analytics #AI',
        media_urls: [
          'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=400&h=400&fit=crop'
        ],
        platforms: ['instagram', 'facebook', 'linkedin'],
        created_by: 'John Doe',
        assigned_reviewer: 'Mike Johnson',
        status: 'pending_review',
        priority: 'high',
        submitted_at: '2024-03-10T14:30:00Z',
        checklist_items: {
          brand_compliance: false,
          content_accuracy: false,
          visual_quality: false,
          platform_specs: false,
          legal_compliance: false
        }
      },
      {
        id: '2',
        post_title: 'Behind the Scenes Video',
        client: 'Fashion Forward',
        post_type: 'video',
        content: 'Take a peek behind the curtain! See how our design team creates magic every day. From concept to creation, every piece tells a story. #BehindTheScenes #Fashion #Design',
        media_urls: [
          'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=400&fit=crop'
        ],
        platforms: ['instagram', 'tiktok'],
        created_by: 'Jane Smith',
        assigned_reviewer: 'Lisa Davis',
        status: 'approved',
        priority: 'medium',
        submitted_at: '2024-03-09T16:45:00Z',
        review_notes: 'Great content! Brand guidelines followed perfectly. Video quality is excellent.',
        checklist_items: {
          brand_compliance: true,
          content_accuracy: true,
          visual_quality: true,
          platform_specs: true,
          legal_compliance: true
        }
      },
      {
        id: '3',
        post_title: 'Weekly Menu Special',
        client: 'Healthy Eats',
        post_type: 'image',
        content: 'This week\'s chef special: Quinoa Buddha Bowl with roasted vegetables and tahini dressing. Packed with nutrients and flavor! Only $12.99. #HealthyEating #BuddaBowl #ChefSpecial',
        media_urls: [
          'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=400&h=400&fit=crop'
        ],
        platforms: ['instagram', 'facebook'],
        created_by: 'Tom Brown',
        assigned_reviewer: 'Sarah Wilson',
        status: 'revision_requested',
        priority: 'medium',
        submitted_at: '2024-03-08T11:20:00Z',
        review_notes: 'Please update the price to $11.99 as per the latest menu. Also, add nutritional information.',
        checklist_items: {
          brand_compliance: true,
          content_accuracy: false,
          visual_quality: true,
          platform_specs: true,
          legal_compliance: true
        }
      }
    ]);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'revision_requested': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const handleApprove = (item: ReviewItem) => {
    setReviewItems(prev => prev.map(i => 
      i.id === item.id 
        ? { ...i, status: 'approved', review_notes: reviewNotes }
        : i
    ));
    setSelectedItem(null);
    setReviewNotes('');
  };

  const handleReject = (item: ReviewItem) => {
    setReviewItems(prev => prev.map(i => 
      i.id === item.id 
        ? { ...i, status: 'rejected', review_notes: reviewNotes }
        : i
    ));
    setSelectedItem(null);
    setReviewNotes('');
  };

  const handleRequestRevision = (item: ReviewItem) => {
    setReviewItems(prev => prev.map(i => 
      i.id === item.id 
        ? { ...i, status: 'revision_requested', review_notes: reviewNotes }
        : i
    ));
    setSelectedItem(null);
    setReviewNotes('');
  };

  const updateChecklistItem = (itemId: string, checklistKey: keyof ReviewItem['checklist_items'], value: boolean) => {
    setReviewItems(prev => prev.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            checklist_items: { ...item.checklist_items, [checklistKey]: value }
          }
        : item
    ));
  };

  const filteredItems = reviewItems.filter(item => {
    switch (activeTab) {
      case 'pending': return item.status === 'pending_review';
      case 'completed': return ['approved', 'rejected', 'revision_requested'].includes(item.status);
      default: return true;
    }
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Quality Review</h1>
        <p className="text-gray-600">Review and approve social media content before publishing</p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'pending', name: 'Pending Review', count: reviewItems.filter(i => i.status === 'pending_review').length },
              { id: 'completed', name: 'Completed', count: reviewItems.filter(i => ['approved', 'rejected', 'revision_requested'].includes(i.status)).length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name} ({tab.count})
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Review Items List */}
        <div className="space-y-4">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow ${
                selectedItem?.id === item.id ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => setSelectedItem(item)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${getPriorityColor(item.priority)}`} />
                  <h3 className="font-semibold text-gray-900">{item.post_title}</h3>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(item.status)}`}>
                  {item.status.replace('_', ' ')}
                </span>
              </div>

              <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                <span>{item.client}</span>
                <span>•</span>
                <span>By {item.created_by}</span>
                <span>•</span>
                <span>{new Date(item.submitted_at).toLocaleDateString()}</span>
              </div>

              <p className="text-gray-600 text-sm mb-3 line-clamp-2">{item.content}</p>

              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  {item.platforms.map((platform) => (
                    <span key={platform} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {platform}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">{item.media_urls.length} media</span>
                  {item.status === 'pending_review' && (
                    <Clock className="h-4 w-4 text-yellow-500" />
                  )}
                  {item.status === 'approved' && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  {item.status === 'rejected' && (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  {item.status === 'revision_requested' && (
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Review Panel */}
        <div>
          {selectedItem ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Review Details</h3>
                <button className="text-blue-600 hover:text-blue-700">
                  <Eye className="h-5 w-5" />
                </button>
              </div>

              {/* Media Preview */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Media Preview</h4>
                <div className="grid grid-cols-2 gap-2">
                  {selectedItem.media_urls.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`Media ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-gray-200"
                    />
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Content</h4>
                <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">{selectedItem.content}</p>
              </div>

              {/* Quality Checklist */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Quality Checklist</h4>
                <div className="space-y-3">
                  {[
                    { key: 'brand_compliance', label: 'Brand Compliance' },
                    { key: 'content_accuracy', label: 'Content Accuracy' },
                    { key: 'visual_quality', label: 'Visual Quality' },
                    { key: 'platform_specs', label: 'Platform Specifications' },
                    { key: 'legal_compliance', label: 'Legal Compliance' }
                  ].map((item) => (
                    <label key={item.key} className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedItem.checklist_items[item.key as keyof typeof selectedItem.checklist_items]}
                        onChange={(e) => updateChecklistItem(selectedItem.id, item.key as keyof typeof selectedItem.checklist_items, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Review Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Review Notes</label>
                <textarea
                  rows={3}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add your review comments..."
                />
              </div>

              {/* Previous Review Notes */}
              {selectedItem.review_notes && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-2">Previous Review Notes</h4>
                  <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">{selectedItem.review_notes}</p>
                </div>
              )}

              {/* Actions */}
              {selectedItem.status === 'pending_review' && (
                <div className="space-y-2">
                  <button
                    onClick={() => handleApprove(selectedItem)}
                    className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <ThumbsUp className="h-4 w-4 mr-2" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleRequestRevision(selectedItem)}
                    className="w-full flex items-center justify-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Request Revision
                  </button>
                  <button
                    onClick={() => handleReject(selectedItem)}
                    className="w-full flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <ThumbsDown className="h-4 w-4 mr-2" />
                    Reject
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
              <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">Select an Item to Review</h3>
              <p className="text-gray-600">Choose an item from the list to start the quality review process</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}